import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { withTranslation } from 'react-i18next'
import {
  Box, Typography, Paper, Button, TextField, InputAdornment,
  IconButton, Menu, MenuItem, ListItemIcon, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress,
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EditIcon from '@mui/icons-material/Edit'
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ArchiveIcon from '@mui/icons-material/Archive'
import DeleteIcon from '@mui/icons-material/Delete'
import BarChartIcon from '@mui/icons-material/BarChart'
import DataTable from 'react-data-table-component'
import {
  getPromotions, publishPromotion as publishPromotionMutation,
  pausePromotion as pausePromotionMutation,
  archivePromotion as archivePromotionMutation,
  duplicatePromotion as duplicatePromotionMutation,
  deletePromotion as deletePromotionMutation,
} from '../apollo'
import { promotionsTheme, PM } from './promotionsTheme'
import CateringBadge from '../components/Common/CateringBadge'

const GET_PROMOTIONS = gql`${getPromotions}`
const PUBLISH = gql`${publishPromotionMutation}`
const PAUSE = gql`${pausePromotionMutation}`
const ARCHIVE = gql`${archivePromotionMutation}`
const DUPLICATE = gql`${duplicatePromotionMutation}`
const DELETE = gql`${deletePromotionMutation}`

// Status badge styles. ACTIVE also gets a dot; anything not listed is neutral.
const BADGE = {
  display: 'inline-flex', alignItems: 'center', gap: '6px', px: '10px', py: '3px',
  borderRadius: '999px', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', lineHeight: 1.4, whiteSpace: 'nowrap'
}
const STATUS_COLORS = {
  ACTIVE: { bgcolor: '#FFF1D6', color: '#92400E' },
  DRAFT: { bgcolor: 'transparent', color: PM.secondary, border: `1px dashed ${PM.inputBorder}`, py: '2px' },
  ARCHIVED: { bgcolor: '#FEECEC', color: PM.danger },
}
const STATUS_NEUTRAL = { bgcolor: PM.chipBg, color: PM.body }

const TABULAR = { fontVariantNumeric: 'tabular-nums' }
const MENU_ITEM = { fontSize: 14, minHeight: 44, borderRadius: '8px', mx: 0.5, my: 0.25, color: PM.text }
const MENU_ICON = { color: PM.icon }
const MENU_ITEM_DANGER = { ...MENU_ITEM, color: PM.danger }
const MENU_ICON_DANGER = { color: PM.danger }
const DIALOG_PAPER = {
  borderRadius: '18px', bgcolor: '#FFFFFF', backgroundImage: 'none', color: PM.text, fontFamily: PM.font,
  boxShadow: '0 24px 60px rgba(15,23,42,0.35)'
}

const PROMO_TYPE_LABELS = {
  ORDER_AMOUNT_OFF: '$ Off Order',
  ORDER_PERCENT_OFF: '% Off Order',
  ITEM_PERCENT_OFF: '% Off Items',
  ITEM_AMOUNT_OFF: '$ Off Items',
  BOGO: 'Buy 1 Get 1',
  BUY_X_GET_Y_PERCENT_OFF: 'Buy X Get Y%',
  BUY_X_SAVE_AMOUNT: 'Buy X Save $',
  FREE_ITEM_ON_THRESHOLD: 'Free Item',
  FREE_DELIVERY_ON_THRESHOLD: 'Free Delivery',
  LOYALTY_REWARD: 'Loyalty Reward',
}

// react-data-table-component styles: header row, 56px body rows, 16px column
// gaps (8px each side) with 20px in front of the first column.
const TABLE_STYLES = {
  table: { style: { backgroundColor: '#FFFFFF' } },
  headRow: {
    style: {
      minHeight: 48, backgroundColor: PM.headBg, borderBottom: `1px solid ${PM.border}`,
      borderBottomStyle: 'solid', borderBottomColor: PM.border, paddingLeft: 12, paddingRight: 12
    }
  },
  headCells: {
    style: {
      paddingLeft: 8, paddingRight: 8,
      fontFamily: PM.font, fontWeight: 700, fontSize: 12, letterSpacing: '0.06em', color: PM.secondary, textTransform: 'uppercase',
      '& div': { overflow: 'visible', whiteSpace: 'nowrap', textOverflow: 'clip' }
    }
  },
  rows: {
    style: {
      minHeight: 56, paddingLeft: 12, paddingRight: 12, fontFamily: PM.font, fontSize: 15, color: PM.text,
      '&:not(:last-of-type)': { borderBottom: `1px solid ${PM.rowBorder}`, borderBottomStyle: 'solid', borderBottomColor: PM.rowBorder }
    },
    highlightOnHoverStyle: {
      backgroundColor: PM.headBg, color: PM.text, transitionDuration: '0.15s', transitionProperty: 'background-color',
      borderBottomColor: PM.rowBorder, outline: 'none'
    }
  },
  cells: { style: { paddingLeft: 8, paddingRight: 8, paddingTop: 8, paddingBottom: 8 } },
  pagination: {
    style: { fontFamily: PM.font, fontSize: 13, color: PM.body, minHeight: 56, borderTop: `1px solid ${PM.border}` },
    pageButtonsStyle: {
      width: 44, height: 44, borderRadius: 10, padding: 10, color: PM.icon, fill: PM.icon,
      '&:disabled': { color: '#C4CAD4', fill: '#C4CAD4' },
      '&:hover:not(:disabled)': { backgroundColor: PM.ghostHover },
      '&:focus': { outline: `2px solid ${PM.focus}`, outlineOffset: 2 }
    }
  }
}

function ActionMenu({ row, onEdit, onPublish, onPause, onArchive, onDuplicate, onAnalytics, onDelete }) {
  const [anchor, setAnchor] = useState(null)
  const close = () => setAnchor(null)
  return (
    <>
      <IconButton size="small" onClick={e => setAnchor(e.currentTarget)}
        sx={{ width: 44, height: 44, borderRadius: '10px', color: PM.secondary, '&:hover': { bgcolor: PM.ghostHover } }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}
        PaperProps={{ elevation: 0, sx: { borderRadius: '12px', boxShadow: '0 12px 28px rgba(15,23,42,0.14)', border: `1px solid ${PM.border}`, minWidth: 190, fontFamily: PM.font } }}>
        <MenuItem onClick={() => { close(); onEdit(row) }} sx={MENU_ITEM}>
          <ListItemIcon><EditIcon fontSize="small" sx={MENU_ICON} /></ListItemIcon>Edit
        </MenuItem>
        {row.status === 'ACTIVE' ? (
          <MenuItem onClick={() => { close(); onPause(row._id) }} sx={MENU_ITEM}>
            <ListItemIcon><PauseIcon fontSize="small" sx={MENU_ICON} /></ListItemIcon>Pause
          </MenuItem>
        ) : (row.status === 'DRAFT' || row.status === 'PAUSED') ? (
          <MenuItem onClick={() => { close(); onPublish(row._id) }} sx={MENU_ITEM}>
            <ListItemIcon><PlayArrowIcon fontSize="small" sx={MENU_ICON} /></ListItemIcon>Publish
          </MenuItem>
        ) : null}
        <MenuItem onClick={() => { close(); onDuplicate(row._id) }} sx={MENU_ITEM}>
          <ListItemIcon><ContentCopyIcon fontSize="small" sx={MENU_ICON} /></ListItemIcon>Duplicate
        </MenuItem>
        <MenuItem onClick={() => { close(); onAnalytics(row) }} sx={MENU_ITEM}>
          <ListItemIcon><BarChartIcon fontSize="small" sx={MENU_ICON} /></ListItemIcon>Analytics
        </MenuItem>
        {row.status !== 'ARCHIVED' && (
          <MenuItem onClick={() => { close(); onArchive(row._id) }} sx={MENU_ITEM_DANGER}>
            <ListItemIcon><ArchiveIcon fontSize="small" sx={MENU_ICON_DANGER} /></ListItemIcon>Archive
          </MenuItem>
        )}
        <MenuItem onClick={() => { close(); onDelete(row) }} sx={MENU_ITEM_DANGER}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={MENU_ICON_DANGER} /></ListItemIcon>Delete
        </MenuItem>
      </Menu>
    </>
  )
}

function AnalyticsDialog({ promo, onClose }) {
  if (!promo) return null
  const a = promo.analytics || {}
  return (
    <Dialog open={Boolean(promo)} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: DIALOG_PAPER }}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: 20, lineHeight: 1.3, color: PM.text, pt: 3 }}>Analytics — {promo.name}</DialogTitle>
      <DialogContent>
        {[
          ['Eligible Carts', a.eligibleCarts ?? 0],
          ['Applied Count', a.appliedCount ?? 0],
          ['Redemption Rate', a.eligibleCarts ? `${Math.round((a.appliedCount / a.eligibleCarts) * 100)}%` : '—'],
          ['Total Discount Given', `$${parseFloat(a.discountTotal || 0).toFixed(2)}`],
          ['Revenue (promo orders)', `$${parseFloat(a.revenueTotal || 0).toFixed(2)}`],
        ].map(([label, value]) => (
          <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1.25, borderBottom: `1px solid ${PM.rowBorder}` }}>
            <Typography variant="body2" sx={{ fontSize: 14, color: PM.secondary }}>{label}</Typography>
            <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 700, color: PM.text, ...TABULAR }}>{value}</Typography>
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${PM.border}` }}>
        <Button onClick={onClose} variant="contained" sx={{ borderRadius: '10px', textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

function Promotions({ t, restaurantId: restaurantIdProp }) {
  const history = useHistory()
  const [search, setSearch] = useState('')
  const [analyticsPromo, setAnalyticsPromo] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const restaurantId = restaurantIdProp || localStorage.getItem('restaurantId') || undefined
  const layoutPrefix = restaurantId ? '/admin' : '/super_admin'

  const { data, loading, refetch } = useQuery(GET_PROMOTIONS, {
    variables: { restaurantId: restaurantId || undefined, page: 0, rowsPerPage: 50 },
    fetchPolicy: 'network-only',
  })

  const [mutatePublish] = useMutation(PUBLISH, { onCompleted: refetch })
  const [mutatePause] = useMutation(PAUSE, { onCompleted: refetch })
  const [mutateArchive] = useMutation(ARCHIVE, { onCompleted: refetch })
  const [mutateDuplicate] = useMutation(DUPLICATE, { onCompleted: refetch })
  const [mutateDelete] = useMutation(DELETE, { onCompleted: () => { setDeleteTarget(null); refetch() } })

  const promotions = (data?.promotions?.promotions || []).filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase())
  )

  const columns = [
    {
      name: 'Name',
      selector: row => row.name,
      sortable: true,
      minWidth: '200px',
      wrap: true,
      cell: row => (
        <Box sx={{ py: 0.5, minWidth: 0 }}>
          {row.isCatering ? (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5 }}>
              <Typography variant="body2" sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.35, color: PM.text, overflowWrap: 'anywhere' }}>{row.name}</Typography>
              <CateringBadge label="Catering deal" />
            </Box>
          ) : (
            <Typography variant="body2" sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.35, color: PM.text, overflowWrap: 'anywhere' }}>{row.name}</Typography>
          )}
          {row.couponCode && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.25, fontSize: 12, fontWeight: 600, color: PM.secondary, fontFamily: 'monospace' }}>
              CODE: {row.couponCode}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      name: 'Type', hide: 'md',
      selector: row => row.promotionType,
      sortable: true,
      minWidth: '140px',
      cell: row => (
        <Box component="span" sx={{ display: 'inline-flex', px: '10px', py: '4px', borderRadius: '8px', bgcolor: PM.chipBg, color: PM.label, fontSize: 13, fontWeight: 600, lineHeight: 1.35, whiteSpace: 'nowrap' }}>
          {PROMO_TYPE_LABELS[row.promotionType] || row.promotionType}
        </Box>
      ),
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      minWidth: '120px',
      cell: row => {
        const s = STATUS_COLORS[row.status] || STATUS_NEUTRAL
        return (
          <Box component="span" sx={{ ...BADGE, ...s }}>
            {row.status === 'ACTIVE' && <Box component="span" aria-hidden sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#D97706', flexShrink: 0 }} />}
            {row.status}
          </Box>
        )
      },
    },
    {
      name: 'Trigger', hide: 'md',
      selector: row => row.triggerType,
      minWidth: '100px',
      cell: row => <Typography variant="caption" sx={{ fontSize: 14, color: PM.body }}>{row.triggerType === 'COUPON_CODE' ? 'Code' : 'Auto'}</Typography>,
    },
    {
      name: 'Period', hide: 'sm',
      minWidth: '176px',
      cell: row => (
        <Typography variant="caption" sx={{ fontSize: 14, color: PM.body, ...TABULAR }}>
          {new Date(row.startAtUtc).toLocaleDateString()}
          {row.endAtUtc ? ` – ${new Date(row.endAtUtc).toLocaleDateString()}` : ' (no end)'}
        </Typography>
      ),
    },
    {
      name: 'Applied', hide: 'sm',
      minWidth: '96px',
      right: true,
      cell: row => <Typography variant="body2" sx={{ fontSize: 15, color: PM.text, ...TABULAR }}>{row.analytics?.appliedCount ?? 0}</Typography>,
    },
    {
      name: 'Discount Given', hide: 'sm',
      minWidth: '150px',
      right: true,
      cell: row => <Typography variant="body2" sx={{ fontSize: 15, fontWeight: 700, color: PM.text, ...TABULAR }}>${parseFloat(row.analytics?.discountTotal || 0).toFixed(2)}</Typography>,
    },
    {
      name: '',
      minWidth: '64px',
      right: true,
      cell: row => (
        <ActionMenu
          row={row}
          onEdit={r => history.push(`${layoutPrefix}/promotion-wizard/${r._id}`)}
          onPublish={id => mutatePublish({ variables: { id } })}
          onPause={id => mutatePause({ variables: { id } })}
          onArchive={id => mutateArchive({ variables: { id } })}
          onDuplicate={id => mutateDuplicate({ variables: { id } })}
          onAnalytics={r => setAnalyticsPromo(r)}
          onDelete={r => setDeleteTarget(r)}
        />
      ),
    },
  ]

  return (
    <ThemeProvider theme={promotionsTheme}>
      <Box sx={{
        minHeight: { xs: 'calc(100vh - 80px)', sm: '100vh' }, bgcolor: PM.page, color: PM.text,
        fontFamily: PM.font, fontSize: 15, lineHeight: 1.45
      }}>
        <Box sx={{
          maxWidth: 1100, mx: 'auto', boxSizing: 'content-box',
          px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 4.5 }, pb: { xs: 5, md: 7 },
          display: 'flex', flexDirection: 'column', gap: 3
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 800, fontSize: { xs: 26, sm: 30 }, letterSpacing: '-0.02em', lineHeight: 1.2, color: PM.text }}>Promotions</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => history.push(`${layoutPrefix}/promotion-wizard`)}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, height: 46, pl: 2, pr: 2.5 }}
            >
              Create Promotion
            </Button>
          </Box>

          <Paper elevation={0} sx={{ borderRadius: '16px', border: `1px solid ${PM.border}`, boxShadow: 'none', overflow: 'hidden', bgcolor: '#FFFFFF' }}>
            <Box sx={{ px: '20px', py: 2, borderBottom: `1px solid ${PM.border}` }}>
              <TextField
                size="small"
                placeholder="Search promotions…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: PM.secondary, fontSize: 20 }} /></InputAdornment>,
                  sx: { borderRadius: '10px', fontSize: 15, '& .MuiOutlinedInput-input': { height: 44 } },
                }}
                sx={{ width: '100%', maxWidth: 360 }}
              />
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <DataTable
                columns={columns}
                data={promotions}
                pagination
                paginationPerPage={20}
                highlightOnHover
                responsive
                customStyles={TABLE_STYLES}
              />
            )}
          </Paper>
        </Box>

        <AnalyticsDialog promo={analyticsPromo} onClose={() => setAnalyticsPromo(null)} />

        {/* Delete confirmation dialog */}
        <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth
          PaperProps={{ sx: DIALOG_PAPER }}>
          <DialogTitle sx={{ fontWeight: 800, fontSize: 20, lineHeight: 1.3, color: PM.text, pt: 3 }}>Delete Promotion?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ fontSize: 15, lineHeight: 1.5, color: PM.body, '& strong': { color: PM.text } }}>
              Are you sure you want to permanently delete <strong>{deleteTarget?.name}</strong>?
              This cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1.25, borderTop: `1px solid ${PM.border}`, '& > :not(style) ~ :not(style)': { ml: 0 } }}>
            <Button onClick={() => setDeleteTarget(null)} variant="outlined"
              sx={{ borderRadius: '10px', textTransform: 'none' }}>Cancel</Button>
            <Button onClick={() => mutateDelete({ variables: { id: deleteTarget._id } })}
              variant="contained" color="error"
              sx={{ borderRadius: '10px', textTransform: 'none' }}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  )
}

export default withTranslation()(Promotions)
